
import moment from 'moment';
import login from '../../modules/auth/application/login';
import AuthError from '../../modules/auth/domain/AuthError';
import createAuthApiRepository from '../../modules/auth/infrastructure/AuthApiRepository';
import orderDetail from '../../libs/mixins/orderDetail';
import saveLoginData from '../../libs/utils/authUtils';
import {
  DEFAULT_MAX_RESENDS,
  DEFAULT_OTP_LENGTH,
  DEFAULT_RESEND_COOLDOWN_SECONDS,
  OTP_CHANNEL_EMAIL,
  OTP_CHANNEL_SMS,
  OTP_ERROR_RESEND_LIMIT,
  PHONE_NUMBER_LENGTH,
  getErrorCard,
  getErrorType,
  getOtpInlineError,
  getOtpInstructionText,
  getRequestInlineError,
  getRequestInstructionText,
  isValidEmailFormat,
  isValidPhoneFormat,
  shouldShowChangeMethodLink,
  shouldShowResendSection,
  toDigits,
} from './otpViewModel';

export default {
  mixins: [orderDetail],

  data() {
    return {
      email: '',
      phone: '',
      otp: '',
      channel: OTP_CHANNEL_EMAIL,
      challengeId: '',
      error: null,
      isLoading: false,
      isOtpInputFocused: false,
      showTutorial: 0,
      showNoticePrivacy: false,
      step: 'request',
      otpLength: DEFAULT_OTP_LENGTH,
      resendTimer: 0,
      resendCount: 0,
      maxResends: DEFAULT_MAX_RESENDS,
      timerId: null,
      OTP_CHANNEL_EMAIL,
      OTP_CHANNEL_SMS,
      PHONE_NUMBER_LENGTH,
    };
  },

  computed: {
    isOtpStep() {
      return this.step === 'otp';
    },

    showPhoneInput() {
      return this.channel === OTP_CHANNEL_SMS;
    },

    requestInstructionText() {
      return getRequestInstructionText();
    },

    otpInstructionText() {
      return getOtpInstructionText(this.channel);
    },

    errorType() {
      return getErrorType(this.error);
    },

    errorCard() {
      return getErrorCard(this.error);
    },

    requestInlineError() {
      if (this.isOtpStep) {
        return '';
      }

      return getRequestInlineError(this.error);
    },

    otpInlineError() {
      if (!this.isOtpStep) {
        return '';
      }

      return getOtpInlineError(this.error);
    },

    showRequestCard() {
      return !this.isOtpStep && Boolean(this.errorCard);
    },

    showOtpCard() {
      return this.isOtpStep && Boolean(this.errorCard) && !this.otpInlineError;
    },

    showResendSection() {
      return shouldShowResendSection(this.error, this.step);
    },

    showChangeMethodLink() {
      return shouldShowChangeMethodLink(this.error, this.step);
    },

    resendLinkLabel() {
      if (this.resendTimer > 0) {
        return `Reenviar en ${this.resendTimer}s`;
      }

      return 'Reenviar';
    },

    isResendDisabled() {
      return this.isLoading
        || this.resendTimer > 0
        || this.resendCount >= this.maxResends
        || this.errorType === OTP_ERROR_RESEND_LIMIT;
    },

    isRequestInputValid() {
      const hasValidEmail = isValidEmailFormat(this.email);

      if (!hasValidEmail) {
        return false;
      }

      if (!this.showPhoneInput) {
        return true;
      }

      return isValidPhoneFormat(this.phone, PHONE_NUMBER_LENGTH);
    },

    isRequestButtonDisabled() {
      return this.isLoading || !this.isRequestInputValid;
    },

    isVerifyButtonDisabled() {
      return this.isLoading || this.otp.length !== this.otpLength;
    },

    otpBoxValues() {
      return Array.from({ length: this.otpLength }, (_, index) => this.otp[index] || '');
    },

    activeOtpBoxIndex() {
      if (!this.isOtpInputFocused) {
        return -1;
      }

      return Math.min(this.otp.length, Math.max(this.otpLength - 1, 0));
    },
  },

  watch: {
    channel(newChannel, previousChannel) {
      this.error = null;

      if (newChannel === previousChannel) {
        return;
      }

      if (newChannel !== OTP_CHANNEL_SMS) {
        this.phone = '';
      }
    },

    isOtpStep(isOtpStep) {
      if (!isOtpStep) {
        this.isOtpInputFocused = false;
        return;
      }

      this.$nextTick(() => {
        this.focusOtpInput();
      });
    },
  },

  beforeDestroy() {
    this.clearResendCountdown();
  },

  methods: {
    selectChannel(channel) {
      if (this.isOtpStep || this.isLoading) {
        return;
      }

      this.channel = channel;
      this.error = null;
    },

    onOtpFocus() {
      this.isOtpInputFocused = true;
    },

    onOtpBlur() {
      this.isOtpInputFocused = false;
    },

    focusOtpInput() {
      if (!this.$refs.otpInput || this.isLoading) {
        return;
      }

      this.$refs.otpInput.focus();
    },

    isOtpBoxActive(index) {
      return index === this.activeOtpBoxIndex;
    },

    updateEmail(value) {
      this.email = String(value || '');
      this.error = null;
    },

    updatePhone(value) {
      const inputValue = String(value || '');

      this.error = null;

      if (!/^[0-9]*$/.test(inputValue)) {
        this.error = new AuthError('Ingresa un número telefónico válido de 10 dígitos (solo números).');
      }

      this.phone = toDigits(inputValue, PHONE_NUMBER_LENGTH);
    },

    updateOtp(value) {
      this.error = null;
      this.otp = toDigits(value, this.otpLength);
    },

    resetOtpFlow() {
      this.step = 'request';
      this.challengeId = '';
      this.otp = '';
      this.resendTimer = 0;
      this.resendCount = 0;
      this.maxResends = DEFAULT_MAX_RESENDS;
      this.error = null;
      this.clearResendCountdown();
    },

    clearResendCountdown() {
      if (this.timerId !== null) {
        clearInterval(this.timerId);
        this.timerId = null;
      }
    },

    startResendCountdown(seconds) {
      this.clearResendCountdown();
      this.resendTimer = seconds;

      if (this.resendTimer <= 0) {
        return;
      }

      this.timerId = setInterval(() => {
        if (this.resendTimer > 0) {
          this.resendTimer -= 1;
        }

        if (this.resendTimer <= 0) {
          this.clearResendCountdown();
        }
      }, 1000);
    },

    validateRequestOtpInput() {
      const emailValue = String(this.email || '').trim();

      if (!emailValue) {
        this.error = new AuthError('Ingresa un correo electrónico.');
        return false;
      }

      if (!isValidEmailFormat(emailValue)) {
        this.error = new AuthError('Ingresa un correo electrónico con formato válido.');
        return false;
      }

      this.email = emailValue;

      if (!this.showPhoneInput) {
        return true;
      }

      if (!this.phone) {
        this.error = new AuthError('Ingresa un número telefónico de 10 dígitos.');
        return false;
      }

      if (!isValidPhoneFormat(this.phone, PHONE_NUMBER_LENGTH)) {
        this.error = new AuthError('Ingresa un número telefónico válido de 10 dígitos (solo números).');
        return false;
      }

      return true;
    },

    async requestOtp(isResend = false) {
      this.error = null;

      if (!this.validateRequestOtpInput()) {
        return;
      }

      this.isLoading = true;

      try {
        const repository = createAuthApiRepository();

        const data = await repository.requestOtp({
          email: this.email,
          orderHigherIdExt: this.$session.get('superOrderId'),
          channel: this.channel,
          phone: this.showPhoneInput ? this.phone : null,
          challengeId: isResend ? this.challengeId : null,
        });

        this.step = 'otp';
        this.challengeId = data.challengeId;
        this.otpLength = data.otpLength || DEFAULT_OTP_LENGTH;
        this.resendCount = data.resendCount || 0;
        this.maxResends = data.maxResends || DEFAULT_MAX_RESENDS;
        this.otp = '';
        this.startResendCountdown(data.cooldownSeconds || DEFAULT_RESEND_COOLDOWN_SECONDS);

        this.$nextTick(() => {
          this.focusOtpInput();
        });
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },

    async verifyOtp() {
      this.isLoading = true;
      this.error = null;

      try {
        const repository = createAuthApiRepository();

        const data = await repository.verifyOtp({
          email: this.email,
          orderHigherIdExt: this.$session.get('superOrderId'),
          challengeId: this.challengeId,
          otp: this.otp,
        });

        if (data?.token) {
          await this.completeLoginFlow(data);
          return;
        }

        await this.login();
      } catch (error) {
        this.error = error;
      } finally {
        this.isLoading = false;
      }
    },

    async login() {
      const repository = createAuthApiRepository();

      const payload = {
        email: this.email,
        orderHigherIdExt: this.$session.get('superOrderId'),
      };

      payload.brand = process.env.VUE_APP_BRAND;

      const data = await login(repository, payload);

      await this.completeLoginFlow(data);
    },

    async completeLoginFlow(data) {
      const superOrderId = this.$session.get('superOrderId');

      this.clearResendCountdown();
      this.saveTokenInSession(data.token, data.expires_in);

      if (data.orders) {
        this.$session.set('historyOrders', data.orders);
      }

      if (!superOrderId) {
        this.$store.commit('SET_LAYOUT', this.getEnvironmentLayoutName());
        this.$session.set('email', this.email);
        this.goToHistory();
        return;
      }

      const { showTutorial, showNoticePrivacy } = saveLoginData(
        this.$session,
        data,
        this.email,
      );

      this.showTutorial = showTutorial;
      this.showNoticePrivacy = showNoticePrivacy;

      await this.getOrderDetail();
    },

    saveTokenInSession(token, expiresIn) {
      localStorage.setItem('token', token);
      this.$session.set('token', token);
      this.$session.set('expire_date', moment().add(expiresIn, 'seconds').format('YYYY-MM-DD HH:mm'));
    },
  },
};
